import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Page } from "../components/AppShell";
import { Badge, Banner, Card, Pager, PanelState } from "../components/ui";
import { date, num, relative, titleCase } from "../lib/format";
import { useApi } from "../lib/useApi";
import type { UsersResponse } from "../lib/types";

const PROFILE_TYPES = [
  "HOME_OWNER",
  "INTERIOR_DESIGNER",
  "RETAILER",
  "DEALER",
  "ARCHITECT",
  "CONTRACTOR",
  "PAINTER",
  "OTHER",
];

/**
 * ── Why the search box only takes three things ─────────────────────────────
 * Names, emails and phone numbers are AES-GCM ciphertext with a fresh IV per
 * row, so no two rows holding "Sharma" share a single byte and there is no
 * LIKE to run. What survives is the blind indexes the schema carries: an exact
 * phone number, an exact email, or the id off a support ticket.
 *
 * The API says so explicitly (`unsearchable: true`) rather than returning an
 * empty list, and this page repeats it — an empty table reads as "no such
 * customer", which is a very different and much more alarming fact than "you
 * can't search by name".
 */
export function CustomersPage() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [profileType, setProfileType] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [sort, setSort] = useState<"recent" | "renders" | "oldest">("recent");
  const [page, setPage] = useState(1);

  // Debounced so a typed phone number is one request, not eleven.
  useEffect(() => {
    const id = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  const { data, error, loading, stale } = useApi<UsersResponse>(
    "/api/admin/users",
    {
      search: search || undefined,
      profileType: profileType || undefined,
      activeOnly: activeOnly ? "1" : undefined,
      sort,
      page,
      pageSize: 25,
    },
  );

  return (
    <Page
      title="Customers"
      subtitle={
        data ? `${num(data.pagination.total)} matching accounts` : "Loading…"
      }
    >
      <div className="filters">
        <input
          className="input"
          style={{ minWidth: 260 }}
          placeholder="Phone, email or customer id"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label="Search customers"
        />
        <select
          className="select"
          value={profileType}
          onChange={(e) => {
            setProfileType(e.target.value);
            setPage(1);
          }}
          aria-label="Filter by customer type"
        >
          <option value="">All types</option>
          {PROFILE_TYPES.map((t) => (
            <option key={t} value={t}>
              {titleCase(t)}
            </option>
          ))}
        </select>
        <select
          className="select"
          value={sort}
          onChange={(e) => setSort(e.target.value as typeof sort)}
          aria-label="Sort"
        >
          <option value="recent">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="renders">Most rooms</option>
        </select>
        <label
          style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}
        >
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => {
              setActiveOnly(e.target.checked);
              setPage(1);
            }}
          />
          Has generated a view
        </label>
      </div>

      {data?.unsearchable && (
        <Banner tone="warning" title="That isn't something we can search on">
          Customer names, emails and phone numbers are encrypted individually,
          so there is no partial-text search to run — two accounts named
          "Sharma" share no bytes in the database. Search takes an exact phone
          number (<span className="mono">9876543210</span> or{" "}
          <span className="mono">+919876543210</span>), an exact email address,
          or a customer id.
        </Banner>
      )}

      <Card>
        <div className={stale ? "stale" : undefined}>
          <PanelState
            loading={loading}
            error={error}
            empty={data?.rows.length === 0 && !data?.unsearchable}
            emptyText="No customers match those filters."
          >
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Type</th>
                    <th>Pincode</th>
                    <th className="num">Rooms</th>
                    <th className="num">Views</th>
                    <th className="num">Hearted</th>
                    <th className="num">Devices</th>
                    <th>Signed up</th>
                    <th>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <Link
                          to={`/customers/${row.id}`}
                          className="cell-strong"
                          style={{ textDecoration: "underline" }}
                        >
                          {row.fullName ?? "Unnamed"}
                        </Link>
                        <div className="cell-mute mono">{row.phone}</div>
                        {row.email && (
                          <div className="cell-mute">{row.email}</div>
                        )}
                      </td>
                      <td className="cell-mute">
                        {titleCase(row.profileType)}
                        {!row.onboarded && (
                          <div>
                            <Badge tone="warning">Incomplete</Badge>
                          </div>
                        )}
                      </td>
                      <td className="mono cell-mute">{row.pincode ?? "—"}</td>
                      <td className="num">{num(row.projects)}</td>
                      <td className="num cell-strong">{num(row.renders)}</td>
                      <td className="num">{num(row.favorites)}</td>
                      <td className="num">
                        {row.liveSessions > 0 ? (
                          <Badge>{row.liveSessions}</Badge>
                        ) : (
                          <span className="cell-mute">0</span>
                        )}
                      </td>
                      <td className="cell-mute">{date(row.createdAt)}</td>
                      <td className="cell-mute">{relative(row.lastLoginAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pager
              meta={data?.pagination}
              onChange={setPage}
              busy={stale}
              label="customers"
            />
          </PanelState>
        </div>
      </Card>
    </Page>
  );
}
